import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import './StaticPage.css';

const LAST_UPDATED = 'March 1, 2025';
const SITE_NAME = 'AstroEpanchang';

const sections = [
  {
    title: '1. Free Services',
    content: `The majority of ${SITE_NAME}'s features — including horoscope generation, daily panchangam, zodiac information, and marriage compatibility — are completely free of charge. No payment is required and therefore no refund applies to these services.`,
  },
  {
    title: '2. Paid Services & Consultations',
    content: 'Where paid consultations or premium reports are offered, the following refund terms apply:',
    items: [
      'Cancellations made more than 24 hours before a scheduled consultation are eligible for a full refund.',
      'Cancellations made within 24 hours of a scheduled consultation are not eligible for a refund.',
      'If a consultation does not take place due to a technical failure on our side, a full refund will be issued within 7 business days.',
      'Digital reports that have already been generated and delivered are non-refundable.',
    ],
  },
  {
    title: '3. How to Request a Refund',
    content: 'To request a refund, please contact us through the Contact page within 7 days of your purchase, providing your order reference and reason for the refund request. We will review your request and respond within 3 business days.',
  },
  {
    title: '4. Refund Processing',
    content: 'Approved refunds will be credited back to the original payment method within 5–10 business days, depending on your payment provider. We are not responsible for delays caused by banks or payment processors.',
  },
  {
    title: '5. Disputes',
    content: 'If you are not satisfied with the outcome of a refund request, please escalate via the contact form and we will endeavour to resolve the matter fairly. This policy does not affect any statutory rights you may have under applicable consumer protection laws.',
  },
];

const Refund = () => {
  const hydrated = useHydrated();
  return (
    <div className="static-page">
      <SEO
        title="Refund Policy"
        description={`${SITE_NAME}'s Refund Policy for paid consultations and premium services. Understand your rights and how to request a refund.`}
        keywords="refund policy, cancellation policy, astroepanchang refund"
        canonicalPath="/refund"
        noindex={false}
      />
      <div className="container">
        <motion.div
          className="static-page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>Refund <span className="gradient-text">Policy</span></h1>
          <p className="static-meta">Last updated: {LAST_UPDATED}</p>
          <p className="static-intro">
            This Refund Policy outlines the conditions under which {SITE_NAME} will issue
            refunds for paid services. Most of our services are free — this policy applies
            to paid consultations and premium features only.
          </p>
        </motion.div>

        <motion.div
          className="static-content"
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          {sections.map((sec, i) => (
            <div key={i} className="static-section">
              <h2>{sec.title}</h2>
              {sec.content && <p>{sec.content}</p>}
              {sec.items && (
                <ul>
                  {sec.items.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              )}
            </div>
          ))}

          <div className="static-cta">
            <p>Have questions about a refund?</p>
            <Link to="/contact" className="btn btn-primary">Contact Us</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Refund;
